/**
 * Animation state driven by sim state (pure, no three.js). Each unit view owns one driver;
 * posers (procedural VRM bones, KayKit mixer + additive layer) read the resulting PoseParams.
 */
import type { RenderUnit } from '../types';
import { BodyYaw, GaitBlend, cadenceHz, footPlant, gaitTarget, shuffleAdvance, twistShares } from './gait';

export type CueKind = 'swing' | 'spell' | 'hit' | 'cancel';

export interface PoseParams {
  /** Seconds since the driver started (drives breathing, sway). */
  t: number;
  /** 0..1 blend toward the run cycle, and its phase in radians (runs backwards while backpedalling). */
  run: number;
  runPhase: number;
  /** Gait mix relative to the rendered body (sum 1): backpedal and side-step shares (the rest is the run). */
  gaitBack: number;
  gaitSide: number;
  /** +1 side-stepping left, -1 right. */
  sideSign: number;
  /** Rendered body heading (sim radians): eased toward the facing, turned off it while strafing. */
  bodyYaw: number;
  /** Spine / head yaw (rad, + = left) turning the upper body back toward the aim. */
  twistSpine: number;
  twistHead: number;
  /** 0..1 turn-in-place shuffle blend and its step phase. */
  shuffle: number;
  shufflePhase: number;
  /** A foot planted this frame: 0 none, 1 left, 2 right (footsteps / dust). */
  plant: 0 | 1 | 2;
  /** 0..1 blend toward the cast pose. */
  cast: number;
  channel: boolean;
  /** Attack progress 0..1 while an attack plays, else -1. */
  attack: number;
  attackKind: 'swing' | 'spell';
  /** 0..1 hit-react intensity (decays). */
  hit: number;
  /** 0..1 death fall progress (0 alive). */
  dead: number;
  /** 0..1 stun blend. */
  stun: number;
  inCombat: boolean;
}

const RUN_REF_SPEED = 7; // yd/s (base move speed)
const ATTACK_S = { swing: 0.5, spell: 0.4 } as const;
const HIT_S = 0.28;
const DEATH_S = 0.65;

const approach = (cur: number, target: number, rate: number, dt: number): number => cur + (target - cur) * (1 - Math.exp(-rate * dt));

export class AnimDriver {
  readonly p: PoseParams = {
    t: 0,
    run: 0,
    runPhase: 0,
    gaitBack: 0,
    gaitSide: 0,
    sideSign: 1,
    bodyYaw: 0,
    twistSpine: 0,
    twistHead: 0,
    shuffle: 0,
    shufflePhase: 0,
    plant: 0,
    cast: 0,
    channel: false,
    attack: -1,
    attackKind: 'swing',
    hit: 0,
    dead: 0,
    stun: 0,
    inCombat: false,
  };
  /** Rendered body heading (gait.ts). */
  readonly body = new BodyYaw();
  private readonly gait = new GaitBlend();
  private attackT = -1;
  private hitT = -1;
  private deathT = -1;
  private wasAlive = true;
  /** +impact: hit-react amplitude of the current react (impact strength). */
  private hitAmp = 1;
  /** +impact: seconds the death fall waits for a held killing blow to land. */
  private deathHold = 0;
  private smoothedSpeed = 0;

  /** `height`: the unit's height (yards), which sets its stride. */
  constructor(
    phaseSeed = 0,
    private readonly height = 2.3,
  ) {
    this.p.t = phaseSeed;
  }

  /** Teleport / respawn: the body snaps to the facing. */
  snapBody(facing: number): void {
    this.body.reset(facing);
  }

  /** `amp` (hit reacts): impact strength, 1 = the classic react; bigger hits stagger further. */
  cue(kind: CueKind, amp = 1): void {
    if (kind === 'cancel') return;
    if (this.deathT >= 0 && !(kind === 'hit' && this.deathHold > 0)) return;
    if (kind === 'hit') {
      // A react already playing only grows (a stream of small hits never shrinks a big stagger).
      this.hitAmp = this.hitT >= 0 && this.hitT < HIT_S * 0.5 ? Math.max(this.hitAmp, amp) : amp;
      this.hitT = 0;
      return;
    }
    // Don't restart a swing that's still in its strike half (keeps fast attackers readable).
    if (this.attackT >= 0 && this.attackT < ATTACK_S[this.p.attackKind] * 0.5) return;
    this.p.attackKind = kind;
    this.attackT = 0;
  }

  /** +impact: the held killing blow landed: fall now. */
  releaseDeath(): void {
    this.deathHold = 0;
  }

  /** +impact: hold the death fall up to `s` seconds (the killing blow is still on its way). */
  deferDeath(s: number): void {
    this.deathHold = Math.max(this.deathHold, s);
    if (this.deathT > 0.12) this.deathHold = 0; // already falling visibly: don't snap back up
    else if (this.deathT > 0) this.deathT = 0;
  }

  /** Advance by `dt` seconds from the unit's current render state. */
  update(u: Pick<RenderUnit, 'alive' | 'speed' | 'cast' | 'cc' | 'inCombat' | 'casting'> & Partial<Pick<RenderUnit, 'facing' | 'vel' | 'steering'>>, dt: number): PoseParams {
    const p = this.p;
    p.t += dt;
    p.inCombat = !!u.inCombat;
    p.plant = 0;
    // Body heading: eased / strafe-turned / turn-in-place (gait.ts); the twist closes the gap to the aim.
    const aim = u.facing ?? 0;
    const moving = u.alive && !u.cc;
    p.bodyYaw = this.body.update({ aim, vel: moving ? u.vel : undefined, steering: u.steering, alive: u.alive }, dt);
    const [spine, head] = twistShares(this.body.gap(aim));
    p.twistSpine = spine;
    p.twistHead = head;
    const turning = this.body.turnStep !== 0;
    p.shufflePhase += shuffleAdvance(this.body.turnStep);
    p.shuffle = approach(p.shuffle, turning ? 1 : 0, turning ? 16 : 8, dt);
    // Death
    if (!u.alive && this.wasAlive) this.deathT = 0;
    if (u.alive && !this.wasAlive) {
      this.deathT = -1;
      p.dead = 0;
    }
    this.wasAlive = u.alive;
    if (this.deathT < 0 && this.deathHold > 0) this.deathHold = Math.max(0, this.deathHold - dt);
    if (this.deathT >= 0 && this.deathHold > 0) {
      // Held: stand (and flinch) until the blow lands, then fall.
      this.deathHold -= dt;
      p.dead = 0;
    } else if (this.deathT >= 0) {
      this.deathT += dt;
      p.dead = Math.min(1, this.deathT / DEATH_S);
      p.run = approach(p.run, 0, 12, dt);
      p.cast = 0;
      p.attack = -1;
      p.stun = 0;
      p.hit = 0;
      return p;
    }
    // Locomotion: smooth the per-tick speed so 20 Hz steps don't jitter the blend.
    this.smoothedSpeed = approach(this.smoothedSpeed, u.speed ?? 0, 14, dt);
    const sp = this.smoothedSpeed;
    p.run = approach(p.run, Math.min(1, sp / (RUN_REF_SPEED * 0.55)), 10, dt);
    // Gait relative to the rendered body (~150 ms smoothstep blends); the cycle runs at speed /
    // stride (feet don't slide), backwards while backpedalling.
    const g = this.gait.update(gaitTarget(moving ? u.vel : undefined, p.bodyYaw), dt);
    p.gaitBack = g.back;
    p.gaitSide = g.side;
    p.sideSign = g.sideSign;
    const dir = g.fwd + g.side - g.back;
    const before = p.runPhase;
    p.runPhase += dt * Math.PI * 2 * cadenceHz(sp, this.height, g.back, g.side) * dir * (p.run > 0.02 ? 1 : 0);
    if (p.run > 0.35 && sp > 1) p.plant = footPlant(before, p.runPhase);
    // Casting
    const casting = !!u.cast || u.casting;
    p.channel = u.cast?.kind === 'channel';
    p.cast = approach(p.cast, casting ? 1 : 0, casting ? 12 : 8, dt);
    // Stun
    p.stun = approach(p.stun, u.cc ? 1 : 0, 10, dt);
    // One-shots
    if (this.attackT >= 0) {
      this.attackT += dt;
      const dur = ATTACK_S[p.attackKind];
      p.attack = this.attackT >= dur ? -1 : this.attackT / dur;
      if (this.attackT >= dur) this.attackT = -1;
    } else p.attack = -1;
    if (this.hitT >= 0) {
      this.hitT += dt;
      p.hit = this.hitT >= HIT_S ? 0 : Math.sin((this.hitT / HIT_S) * Math.PI) * (1 - this.hitT / HIT_S * 0.3) * this.hitAmp;
      if (this.hitT >= HIT_S) this.hitT = -1;
    }
    return p;
  }
}

const SWING_OUT = { raise: 0, strike: 0 };
const swingOut = (raise: number, strike: number): { raise: number; strike: number } => {
  SWING_OUT.raise = raise;
  SWING_OUT.strike = strike;
  return SWING_OUT;
};

/**
 * Melee swing curve (mobs; heroes play ability clips, see anim/): wind-up (raise) then a fast
 * strike. Returns the arm "raise" amount -1..1 (1 = overhead). The result is a shared scratch
 * object (no allocation): read it before the next call.
 */
export function swingCurve(a: number): { raise: number; strike: number } {
  if (a < 0) return swingOut(0, 0);
  if (a < 0.4) {
    const k = a / 0.4;
    return swingOut(k * k * (3 - 2 * k), 0);
  }
  if (a < 0.6) {
    const k = (a - 0.4) / 0.2;
    return swingOut(1 - 1.6 * k, k);
  }
  const k = (a - 0.6) / 0.4;
  return swingOut(-0.6 * (1 - k), 1 - k);
}

export const easeInCubic = (x: number): number => x * x * x;
